import { ApolloLink, InMemoryCacheConfig, Operation } from '@apollo/client/core'
import { HttpLink } from 'apollo-angular/http'
import { mergeDeep } from '@apollo/client/utilities/internal'
import { mergeDeepRight } from 'ramda'
import { GraphQLError } from 'graphql'
import { Observable } from 'rxjs'
import { tap } from 'rxjs/operators'

import { UntypedFormGroup } from '@angular/forms'

import { EnvironmentVariables } from '@app-types/environment.types'
import { AuthStateStore } from '@app-services'
import { contentBlockTypes } from '@app-domains/content-blocks/types'

/**
 * Constructs a GraphQL API URL for the given schema and hostname. Optionally takes a custom path
 * for the URL (default 'graphql')
 */
export const graphQlApiUrl = (env: { schema: string; hostname: string }, path: string = 'graphql') =>
    (operation: Operation): string => `${env.schema}://${env.hostname}/${path}?op=${operation.operationName}`

/**
 * Creates an {@link HttpLink Apollo HTTP link} for the given environment.
 */
export function createApiHttpLink(httpLink: HttpLink, environment: EnvironmentVariables) {
    return httpLink.create({
        uri: graphQlApiUrl(environment.api),
    })
}

export function createSubscriptionsHttpLink(httpLink: HttpLink, environment: EnvironmentVariables) {
    return httpLink.create({
        uri: graphQlApiUrl({
            schema: environment.subscriptionGraphql.schema,
            hostname: `${environment.subscriptionGraphql.hostname}/graphql`,
        }),
    })
}

export function createApiAuthLink(authStorageService: AuthStateStore): ApolloLink {
    return new ApolloLink((operation, forward) => {
        const context = operation.getContext()
        const token = authStorageService.getAuthState()?.accessToken
        const authContext = token
            ? mergeDeepRight(context, {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            })
            : context

        operation.setContext(authContext as Parameters<typeof operation.setContext>[0])
        return forward(operation)
    })
}

export interface ValidationError {
    field: string
    message: Array<string>
}

export const createValidationError = (
    error: GraphQLError | Record<string, string[]>,
): Array<ValidationError> => {
    const entries = Object.entries(error)
    return entries.map(([field, message]) => ({
        field: field.replace(/^input\./g, ''),
        message,
    } as ValidationError))
}

export const connectForm = <TData = any>(
    obs: Observable<{
        data?: TData
        error?: { errors?: readonly any[]; message?: string } | Error
        errors?: readonly any[]
        extensions?: Record<string, any>
    }>,
    form: UntypedFormGroup,
) => obs.pipe(
    tap((response) => {
        const validationErrors = response.extensions?.validationErrors as Array<ValidationError>
        if (validationErrors) {
            validationErrors.forEach((error) => {
                form.get(error.field)?.setErrors({ ...error.message })
            })
        }
        const graphqlErrors = response.errors
            ?? (response.error && 'errors' in response.error ? response.error.errors : undefined)
        const generalErrors = graphqlErrors?.filter(
            (err) => err.extensions?.category !== 'validation',
        ).map((err) => err.message)
        form.setErrors({ ...generalErrors })
    }),
)

export const cacheConfig: InMemoryCacheConfig = {
    typePolicies: {
        Product: {
            keyFields: ['ean'],
            fields: {
                categories: {
                    merge(existing = [], incoming = []) {
                        return [...existing, ...incoming]
                    },
                },
                images: {
                    // Every query selects the complete image list, so the incoming list replaces the existing one.
                    // Merging would keep images restored from the server-rendered snapshot in front of the fresh
                    // ones, so a product would keep showing images that were changed or removed in Katana.
                    merge(_existing, incoming) {
                        return incoming
                    },
                },
            },
        },
        Debtor: {
            keyFields: ['debtorCode'],
        },
        PositionedPages: {
            keyFields: ['identifier'],
        },
        Page: {
            keyFields: ['slug', 'locale'],
        },
        Category: {
            merge: (existing, incoming) => {
                return mergeDeep(existing, incoming)
            },
            fields: {
                productImage: {
                    merge(existing, incoming) {
                        return incoming !== undefined ? incoming : existing
                    },
                },
            },
        },
        CategoryTranslationsMap: {
            merge: (existing, incoming) => {
                return mergeDeep(existing, incoming)
            },
        },
    },
    possibleTypes: {
        Component: contentBlockTypes,
    },
}
